import React, { forwardRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  InputGroup, Dropdown, Spinner, Form,
} from 'react-bootstrap';
import classNames from 'classnames';
import SearchInput from './searchInput';

const StyledDropdownMenu = styled(Dropdown.Menu)`
  max-height: min(20em, 50vh);
  overflow: auto;
  z-index: 1031;
`;

export const valuePropType = PropTypes
  .oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]);

const Selector = forwardRef(({
  id, value, values, onChange, disabled, readOnly,
  required, isLoading, nullLabel, onDropDown, errors,
  className, size, errorAsTooltip, onFocus, onBlur, searchable,
  prepend, append,
}, ref) => {
  const [search, setSearch] = useState('');
  const valueTest = useMemo(
    () => (typeof value === 'object' && value !== null ? value.id : value),
    [value],
  );
  const displayValues = useMemo(
    () => (required ? values || [] : [{ value: null, display_name: nullLabel }, ...values])
      .filter((v) => !search || v.display_name.toLowerCase().indexOf(search.toLowerCase()) > -1)
      .map((v) => ({
        ...v,
        valueTest: typeof v.value === 'object' && v.value !== null ? v.value.id : v.value,
      })),
    [nullLabel, required, search, values],
  );
  const displayValue = useMemo(
    () => displayValues.reduce(
      (R, v) => (v.valueTest === valueTest ? v.display_name : R),
      typeof value === 'object' && value !== null ? value.repr : '',
    ),
    [displayValues, value, valueTest],
  );
  const errored = !!errors && !!errors.length;

  return (
    <InputGroup className={className} size={size || 'sm'}>
      {prepend}
      <div
        id={id}
        ref={ref}
        className={classNames(className, 'form-control d-flex gap-2', { 'is-invalid': errored })}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {displayValue}
      </div>
      {append}
      {isLoading && (
        <div className="border border-left-0 px-2 d-flex align-items-center">
          <Spinner animation="border" size={size || 'sm'} />
        </div>
      )}
      <Dropdown
        // style={{ position: 'unset' }}
        // as={InputGroup.Append}
        size={size || 'sm'}
        onToggle={(isOpen, e, md) => {
          if (isOpen && onDropDown) onDropDown(e, md);
        }}
      >
        <Dropdown.Toggle id={`drdn-${id}`} variant="light" disabled={disabled || readOnly} split size="sm" />
        <StyledDropdownMenu align="end" className="scrollbar">
          {searchable && (
            <div className="mx-2">
              <SearchInput onSearch={setSearch} />
            </div>
          )}
          <Dropdown.Divider />
          {displayValues.map((vv) => (
            <Dropdown.Item
              key={vv.valueTest}
              active={vv.valueTest === valueTest}
              disabled={vv.disabled}
              onClick={(e) => { e.preventDefault(); onChange(e, vv.value); }}
              onSelect={(eventKey, event) => event.preventDefault()}
              onFocus={onFocus}
              onBlur={onBlur}
            >
              {vv.display_name}
            </Dropdown.Item>
          ))}
        </StyledDropdownMenu>
      </Dropdown>
      {errors && (
      <Form.Control.Feedback type="invalid" tooltip={errorAsTooltip}>
        {errors.join(', ')}
      </Form.Control.Feedback>
      )}
    </InputGroup>
  );
});

Selector.propTypes = {
  id: PropTypes.string,
  value: valuePropType,
  values: PropTypes.arrayOf(PropTypes.shape({
    value: valuePropType,
    disabled: PropTypes.bool,
    display_name: PropTypes.string,
  })),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  isLoading: PropTypes.bool,
  nullLabel: PropTypes.string,
  onDropDown: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  errors: PropTypes.arrayOf(PropTypes.string),
  errorAsTooltip: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'lg', '']),
  prepend: PropTypes.node,
  append: PropTypes.node,
  searchable: PropTypes.bool,
};

Selector.defaultProps = {
  id: '',
  value: null,
  values: [],
  onChange: () => null,
  disabled: false,
  readOnly: false,
  required: true,
  isLoading: false,
  nullLabel: 'Not selected',
  onDropDown: null,
  className: '',
  size: 'sm',
  errors: null,
  onFocus: null,
  onBlur: null,
  errorAsTooltip: false,
  prepend: null,
  append: null,
  searchable: true,
};

export default Selector;
